import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../../store/authSlice";
import { X, Menu, ChevronDown, ChevronRight, Loader2, User, LogOut } from "lucide-react";
import axios from "axios";
import vendbabeeLogo from "../../assets/vendbabee-vending-logo.webp";
import PromoBanner from "../PromoBanner";
import CartIcon from "../cart/CartIcon";
import CartDropdown from "../cart/CartDropdown";



const URL = process.env.BACKEND_URL;
const API = `${URL}/api`;

/* ── All categories flattened — each one gets its own nav slot ──────────── */
const ALL_CATEGORIES = [
  { label: "Combo Snack + Drink", slug: "Combo Snack+Drink", group: "Vending Machines" },
  { label: "Standard Snack",      slug: "Standard Snack",    group: "Vending Machines" },
  { label: "Refrigerated",        slug: "Refrigerated",      group: "Vending Machines" },
  { label: "Frozen",              slug: "Frozen",            group: "Vending Machines" },
  { label: "Custom",              slug: "Custom",            group: "Vending Machines" },
  { label: "Arcade Games",        slug: "Arcade Game",       group: "Arcade & Entertainment" },
  { label: "Pinball",             slug: "Pinball Machine",   group: "Arcade & Entertainment" },
  { label: "Prize Redemption",    slug: "Prize Redemption",  group: "Arcade & Entertainment" },
  { label: "Crane / Claw",        slug: "Crane/Claw Machine",group: "Arcade & Entertainment" },
  { label: "Token Dispensers",    slug: "Token Dispenser",   group: "Arcade & Entertainment" },
  { label: "Ticket Dispensers",   slug: "Ticket Dispenser",  group: "Arcade & Entertainment" },
];

const MAX_PRODUCTS_PER_CATEGORY = 10;

/* ── Shared product fetch hook ─────────────────────────────────────────── */
function useProductsByCategory() {
  const [catMap, setCatMap] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    axios
      .get(`${API}/products`, { params: { status: "Active" } })
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        const map = {};
        all.forEach((p) => {
          if (!p.machine_type) return;
          if (!map[p.machine_type]) map[p.machine_type] = [];
          if (map[p.machine_type].length < MAX_PRODUCTS_PER_CATEGORY) {
            map[p.machine_type].push(p);
          }
        });
        setCatMap(map);
      })
      .catch(() => setCatMap({}));
  }, []);

  return catMap;
}

/* ── User Menu Dropdown ────────────────────────────────────────────────── */
function UserMenuDropdown({ onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);

  const handleLogout = () => {
    dispatch(logoutUser()).then(() => {
      onClose();
      navigate("/");
    });
  };

  return (
    <div
      className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-border bg-card py-2 z-[110]"
      style={{ boxShadow: "var(--glow-card)" }}
    >
      <div className="px-4 py-3 border-b border-border">
        <p className="font-body text-sm font-semibold text-foreground truncate">
          {user?.first_name} {user?.last_name}
        </p>
        <p className="font-body text-xs text-muted-foreground truncate mt-0.5">
          {user?.email}
        </p>
      </div>
      <button
        onClick={() => { onClose(); navigate("/account"); }}
        className="w-full flex items-center gap-3 px-4 py-2.5 font-body text-sm text-foreground hover:bg-secondary transition-colors text-left"
        style={{ background: "none", border: "none", cursor: "pointer" }}
      >
        <User className="h-4 w-4 text-muted-foreground" />
        My Account
      </button>
      <div className="mx-3 my-1 border-t border-border" />
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-4 py-2.5 font-body text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors text-left"
        style={{ background: "none", border: "none", cursor: "pointer" }}
      >
        <LogOut className="h-4 w-4" />
        Sign Out
      </button>
    </div>
  );
}

/* ── Mobile Drawer ──────────────────────────────────────────────────────── */
function MobileDrawer({ open, onClose, catMap }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const [expandedSlug, setExpandedSlug] = useState(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleLogout = () => {
    dispatch(logoutUser()).then(() => {
      onClose();
      navigate("/");
    });
  };

  const groups = {};
  ALL_CATEGORIES.forEach((c) => {
    if (!groups[c.group]) groups[c.group] = [];
    groups[c.group].push(c);
  });

  return (
    <>
      <div
        className={`drawer-overlay ${open ? "open" : ""}`}
        onClick={onClose}
      />
      {/* Drawer slides in from LEFT */}
      <div
        className={`drawer-nav ${open ? "open" : ""}`}
        style={{ left: 0, right: "auto" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <img
            src={vendbabeeLogo}
            alt="Vendbabee"
            style={{ height: 32, width: 32, objectFit: "contain" }}
          />
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center border border-border text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {Object.entries(groups).map(([groupName, cats]) => (
            <div key={groupName}>
              <p className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {groupName}
              </p>
              {cats.map((cat) => {
                const prods = catMap?.[cat.slug] || [];
                const isExpanded = expandedSlug === cat.slug;
                return (
                  <div key={cat.slug}>
                    <button
                      onClick={() =>
                        setExpandedSlug(isExpanded ? null : cat.slug)
                      }
                      className="w-full flex items-center justify-between px-5 py-3 font-body text-sm font-semibold text-foreground hover:bg-secondary transition-colors"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                      }}>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-1 h-1 rounded-full flex-shrink-0"
                          style={{ background: "var(--vb-indigo)" }}
                        />
                        <span>{cat.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {prods.length > 0 && (
                          <span className="font-mono text-[9px] text-muted-foreground">
                            {prods.length}
                          </span>
                        )}
                        <ChevronDown
                          className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200"
                          style={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="pb-2">
                        {prods.length === 0 ? (
                          <p className="pl-12 pr-5 py-2 font-body text-xs text-muted-foreground">
                            No machines listed yet
                          </p>
                        ) : (
                          <>
                            {prods.map((prod) => (
                              <button
                                key={prod.mongo_id || prod.id}
                                onClick={() => {
                                  onClose();
                                  navigate(
                                    `/product/${prod.mongo_id || prod.id}`,
                                  );
                                }}
                                className="w-full flex items-center gap-3 pl-10 pr-5 py-2.5 font-body text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors text-left"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                }}>
                                <div
                                  style={{
                                    width: 32,
                                    height: 24,
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    background: "#f0efff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}>
                                  {prod.img_url ||
                                  (prod.photo_urls && prod.photo_urls[0]) ? (
                                    <img
                                      src={prod.img_url || prod.photo_urls[0]}
                                      alt=""
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <span
                                      style={{ fontSize: 7, color: "#9d8fff" }}>
                                      IMG
                                    </span>
                                  )}
                                </div>
                                <span className="flex-1 truncate">
                                  {prod.product_name}
                                </span>
                                {prod.sale_price && (
                                  <span
                                    className="flex-shrink-0"
                                    style={{
                                      color: "var(--vb-indigo)",
                                      fontFamily: "'DM Mono',monospace",
                                      fontSize: 11,
                                    }}>
                                    ${prod.sale_price.toLocaleString()}
                                  </span>
                                )}
                              </button>
                            ))}
                            <a
                              href={`/shop?category=${encodeURIComponent(cat.slug)}`}
                              onClick={onClose}
                              className="flex items-center gap-2 pl-10 pr-5 py-2 font-body text-xs font-semibold transition-colors"
                              style={{
                                textDecoration: "none",
                                color: "var(--vb-indigo)",
                              }}>
                              View all {cat.label}{" "}
                              <ChevronRight className="h-3 w-3" />
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="mx-5 my-3 border-t border-border" />
          {isAuthenticated ? (
            <>
              <button
                onClick={() => {
                  onClose();
                  navigate("/account");
                }}
                className="w-full flex items-center gap-3 px-5 py-3.5 font-body font-semibold text-sm text-foreground hover:bg-secondary transition-colors text-left"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--grad-primary)" }}>
                  <span className="text-white text-xs font-semibold">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block truncate">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className="block text-xs text-muted-foreground font-normal truncate">
                    {user?.email}
                  </span>
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-3.5 font-body text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors text-left"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}>
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </>
          ) : null}
        </div>

        <div className="px-5 py-5 border-t border-border">
          <a
            href="/#quote"
            onClick={onClose}
            className="btn-primary w-full justify-center"
            style={{ textDecoration: "none" }}>
            Get Business Pricing
          </a>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Navbar Root
   ═══════════════════════════════════════════════════════════════════════════ */
export default function Navbar({ ctaLabel = "Get Business Pricing", onCtaClick, transparent = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((s) => s.auth);

  const catMap = useProductsByCategory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const userMenuRef = useRef(null);

  // Track scroll position for transparent → solid transition
  useEffect(() => {
    if (!transparent) return;
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [transparent]);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target))
        setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const isTransparentActive = transparent && !scrolled;

  return (
    <>
      {/* Promo banner */}
      {(!transparent || scrolled) && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 102 }}>
          <PromoBanner />
        </div>
      )}

      <nav
        className="nav-root"
        style={
          isTransparentActive
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                height: "var(--nav-h)",
                background: "transparent",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
                borderBottom: "1px solid transparent",
                boxShadow: "none",
                transition:
                  "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
              }
            : undefined
        }>
        {/* LEFT: hamburger on mobile, logo always */}
        <div className="flex items-center gap-2">
          {/* Hamburger — LEFT side on mobile */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl border border-border text-foreground hover:bg-secondary transition-colors">
            <Menu className="h-5 w-5" />
          </button>

          <img
            src={vendbabeeLogo}
            alt="Vendbabee"
            onClick={() => navigate("/")}
            style={{
              /* Reduced ~45% on mobile, full size on desktop */
              height: "clamp(26px, 5vw, 46px)",
              width: "auto",
              objectFit: "contain",
              cursor: "pointer",
            }}
          />
        </div>

        {/* RIGHT: user menu + cart (no "Get Business Pricing" CTA in navbar) */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <div className="hidden md:block relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-secondary transition-colors"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "var(--grad-primary)" }}>
                  <span className="text-white text-xs font-semibold font-body">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </span>
                </div>
                <span className="font-body text-sm font-medium text-foreground hidden lg:inline">
                  {user?.first_name}
                </span>
                <ChevronDown
                  className="h-3 w-3 text-muted-foreground transition-transform duration-200"
                  style={{
                    transform: userMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
              {userMenuOpen && (
                <UserMenuDropdown onClose={() => setUserMenuOpen(false)} />
              )}
            </div>
          ) : null}

          <CartIcon />
        </div>
      </nav>

      <CartDropdown />
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        catMap={catMap}
      />
    </>
  );
}
